import {
  achievementHovercardPath,
  advisoryHovercardPath,
  automatedReviewerHovercardPath,
  commitHovercardPath,
  copilotHovercardPath,
  cweHovercardPath,
  dependabotAlertHovercardPath,
  dependencyGraphPackageHovercardPath,
  discussionHovercardPath,
  integrationHovercardPath,
  issueHovercardPath,
  issueLinkedPullRequestHovercardPath,
  orgHovercardPath,
  profileHighlightHovercardPath,
  projectHovercardPath,
  repositoryPath,
  securityAlertHovercardPath,
  sponsorsListingHovercardPath,
  teamHovercardPath,
  trackedInHovercardPath,
  trackingHovercardPath,
  userHovercardPath,
  userIdHovercardPath,
} from '@github-ui/paths'
import {useCallback} from 'react'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'

import {HOVERCARD_KEYBOARD_SHORTCUT, isHovercardHintEnabledForUser, isHovercardEnabledForUser} from './preferences'
import {getAutomatedReviewer} from './automated-reviewers'

/**
 * Base options common to all hovercard types.
 */
export type BaseHovercardOptions = {
  /** Include octo-click/octo-dimensions tracking attributes. Default: true */
  tracking?: boolean
}

/**
 * Additional hovercard configuration attributes that can be set on elements.
 * These control hovercard behavior but don't affect which hovercard is shown.
 */
export type HovercardConfigOptions = {
  /** Use fixed positioning for the hovercard popover */
  fixedPositioning?: boolean
  /** Override the z-index of the hovercard popover */
  zIndexOverride?: number
  /** Cache key/version for the hovercard content */
  cache?: string
}

/**
 * Hovercard attributes without tracking.
 */
export type HovercardAttributes = {
  'data-hovercard-url': string
  'data-hovercard-type': string
}

/**
 * Hovercard attributes with analytics tracking.
 */
export type HovercardAttributesWithTracking = HovercardAttributes & {
  'octo-click': string
  'octo-dimensions': string
}

/**
 * Hovercard attributes with optional configuration.
 */
export type HovercardAttributesWithConfig = (HovercardAttributes | HovercardAttributesWithTracking) & {
  'data-hovercard-fixed-positioning'?: ''
  'data-hovercard-z-index-override'?: string
  'data-hovercard-cache'?: string
  'aria-keyshortcuts'?: string
}

/**
 * Result type for hovercard attribute functions.
 * Returns empty object when hovercard cannot be generated (missing params, disabled, server render).
 */
export type HovercardAttributesResult =
  | HovercardAttributes
  | HovercardAttributesWithTracking
  | HovercardAttributesWithConfig
  | Record<string, never>

// Stable empty result for server/disabled cases - avoids creating new objects
const EMPTY_RESULT: HovercardAttributesResult = Object.freeze({})

/**
 * Add keyboard hint aria attribute if enabled for the user.
 */
export function addKeyboardHint<T extends Record<string, string>>(attributes: T): T & {'aria-keyshortcuts'?: string} {
  if (isHovercardHintEnabledForUser()) {
    return {...attributes, 'aria-keyshortcuts': HOVERCARD_KEYBOARD_SHORTCUT}
  }
  return attributes
}

/**
 * Add analytics tracking attributes to hovercard attributes.
 */
export function addTrackingAttributes(attributes: HovercardAttributes): HovercardAttributesWithTracking {
  return addKeyboardHint({
    ...attributes,
    'octo-click': 'hovercard-link-click',
    'octo-dimensions': 'link_type:self',
  })
}

/**
 * Add configuration attributes to hovercard attributes.
 */
function addConfigAttributes(
  attributes: HovercardAttributes | HovercardAttributesWithTracking,
  config?: HovercardConfigOptions,
): HovercardAttributesResult {
  if (!config) return attributes

  const result: HovercardAttributesWithConfig = {...attributes}

  if (config.fixedPositioning) {
    result['data-hovercard-fixed-positioning'] = ''
  }
  if (config.zIndexOverride !== undefined) {
    result['data-hovercard-z-index-override'] = String(config.zIndexOverride)
  }
  if (config.cache) {
    result['data-hovercard-cache'] = config.cache
  }

  return result
}

/**
 * Build final hovercard attributes with optional tracking.
 */
export function buildHovercardAttributes(
  url: string,
  type: string,
  tracking: boolean = true,
): HovercardAttributesResult {
  const attributes: HovercardAttributes = {
    'data-hovercard-url': url,
    'data-hovercard-type': type,
  }

  if (tracking) {
    return addTrackingAttributes(attributes)
  }
  return addKeyboardHint(attributes)
}

/**
 * Build final hovercard attributes with optional tracking and configuration.
 */
export function buildHovercardAttributesWithConfig(
  url: string,
  type: string,
  tracking: boolean = true,
  config?: HovercardConfigOptions,
): HovercardAttributesResult {
  const baseAttributes = buildHovercardAttributes(url, type, tracking)
  if (Object.keys(baseAttributes).length === 0) return EMPTY_RESULT
  return addConfigAttributes(baseAttributes as HovercardAttributes | HovercardAttributesWithTracking, config)
}

/**
 * Hook wrapper that handles server-side rendering and user preferences.
 * Returns EMPTY_RESULT on server or when hovercards are disabled.
 */
export function useHovercardAttributesBase(attributes: HovercardAttributesResult): HovercardAttributesResult {
  const {isServer} = useRuntimeEnvironment()

  if (isServer || !isHovercardEnabledForUser()) {
    return EMPTY_RESULT
  }

  return attributes
}

/**
 * Get an empty hovercard result. Use when conditions aren't met.
 */
export function emptyHovercardResult(): HovercardAttributesResult {
  return EMPTY_RESULT
}

// =============================================================================
// Unified hook types and implementation
// =============================================================================

/**
 * Supported hovercard types for the unified hook.
 */
export type UnifiedHovercardType =
  | 'user'
  | 'organization'
  | 'team'
  | 'issue'
  | 'pull_request'
  | 'commit'
  | 'copilot'
  | 'automated_reviewer'
  | 'repository'
  | 'discussion'
  | 'advisory'
  | 'cwe'
  | 'sponsors_listing'
  | 'achievement'
  | 'tracked_in'
  | 'tracking'
  | 'security_alert'
  | 'dependabot_alert'
  | 'dependency_graph_package'
  | 'project'
  | 'integration'
  | 'profile_highlight'

// Type-specific option payloads for the unified hook
type UserLoginParams = {login: string | undefined; userId?: never}
type UserIdParams = {userId: number | undefined; login?: never}

type UnifiedHovercardParams = {
  user: UserLoginParams | UserIdParams
  organization: {login: string | undefined}
  team: {owner: string | undefined; team: string | undefined} | {url: string}
  issue: {owner: string | undefined; repo: string | undefined; issueNumber: number | undefined} | {url: string}
  pull_request:
    | {owner: string | undefined; repo: string | undefined; pullRequestNumber: number | undefined}
    | {url: string}
  commit: {owner: string | undefined; repo: string | undefined; commitish: string | undefined}
  copilot: {botSlug: string | undefined}
  automated_reviewer: {botSlug: string | undefined}
  repository: {owner: string | undefined; repo: string | undefined}
  discussion: {owner: string | undefined; repo: string | undefined; discussionNumber: number | undefined}
  advisory: {ghsaId: string | undefined}
  cwe: {id: string | undefined}
  sponsors_listing: {login: string | undefined}
  achievement: {login: string | undefined; slug: string | undefined}
  tracked_in: {owner: string | undefined; repo: string | undefined; issueNumber: number | undefined} | {url: string}
  tracking: {
    owner: string | undefined
    repo: string | undefined
    issueNumber: number | undefined
    id: number | undefined
  }
  security_alert: {owner: string | undefined; repo: string | undefined; alertNumber: number | undefined}
  dependabot_alert: {owner: string | undefined; repo: string | undefined; alertNumber: number | undefined}
  dependency_graph_package: {
    owner: string | undefined
    repo: string | undefined
    id: string | undefined
    name: string | undefined
  }
  project: {projectId: number | undefined}
  integration: {id: number | undefined}
  profile_highlight: {login: string | undefined; highlightType: string | undefined}
}

/**
 * URL override: pass a pre-computed hovercard URL directly instead of type-specific params.
 */
type UrlOverrideParams = {url: string}

/**
 * Options for the unified hovercard function.
 * Either pass type-specific params (e.g. {login} for user) or a URL override ({url}).
 */
export type UnifiedHovercardOptions<T extends UnifiedHovercardType> = (UnifiedHovercardParams[T] | UrlOverrideParams) &
  BaseHovercardOptions &
  HovercardConfigOptions

/**
 * Function signature returned by useGetHovercardAttributesForType.
 */
export type GetHovercardAttributesForTypeFn = <T extends UnifiedHovercardType>(
  type: T,
  options: UnifiedHovercardOptions<T>,
) => HovercardAttributesResult

/**
 * Generate the URL for a hovercard based on type and options.
 */
function getHovercardUrlForType<T extends UnifiedHovercardType>(
  type: T,
  options: UnifiedHovercardOptions<T>,
): string | null {
  // URL override: works for any type. Caller provides a pre-computed URL.
  const anyOpts = options as Record<string, unknown>
  if (typeof anyOpts.url === 'string' && anyOpts.url) {
    // For types that have their own url-based variant (issue, PR, team, tracked_in),
    // this handles them uniformly. For other types, it provides a generic override.
    return anyOpts.url
  }

  switch (type) {
    case 'user': {
      const opts = options as UnifiedHovercardOptions<'user'>
      if ('userId' in opts && opts.userId != null) {
        return userIdHovercardPath({userId: opts.userId})
      }
      if ('login' in opts && opts.login) {
        return userHovercardPath({owner: opts.login})
      }
      return null
    }
    case 'organization': {
      const opts = options as UnifiedHovercardOptions<'organization'>
      if ('login' in opts && opts.login) {
        return orgHovercardPath({owner: opts.login})
      }
      return null
    }
    case 'team': {
      const opts = options as UnifiedHovercardOptions<'team'>
      if ('owner' in opts && opts.owner && 'team' in opts && opts.team) {
        return teamHovercardPath({owner: opts.owner, team: opts.team})
      }
      return null
    }
    case 'issue': {
      const opts = options as UnifiedHovercardOptions<'issue'>
      if (
        'owner' in opts &&
        opts.owner &&
        'repo' in opts &&
        opts.repo &&
        'issueNumber' in opts &&
        opts.issueNumber != null
      ) {
        return issueHovercardPath({owner: opts.owner, repo: opts.repo, issueNumber: opts.issueNumber})
      }
      return null
    }
    case 'pull_request': {
      const opts = options as UnifiedHovercardOptions<'pull_request'>
      if (
        'owner' in opts &&
        opts.owner &&
        'repo' in opts &&
        opts.repo &&
        'pullRequestNumber' in opts &&
        opts.pullRequestNumber != null
      ) {
        return issueLinkedPullRequestHovercardPath({
          owner: opts.owner,
          repo: opts.repo,
          pullRequestNumber: opts.pullRequestNumber,
        })
      }
      return null
    }
    case 'commit': {
      const opts = options as UnifiedHovercardOptions<'commit'>
      if ('owner' in opts && opts.owner && 'repo' in opts && opts.repo && 'commitish' in opts && opts.commitish) {
        return commitHovercardPath({owner: opts.owner, repo: opts.repo, commitish: opts.commitish})
      }
      return null
    }
    case 'copilot': {
      const opts = options as UnifiedHovercardOptions<'copilot'>
      if ('botSlug' in opts && opts.botSlug) return copilotHovercardPath({bot_slug: opts.botSlug})
      return null
    }
    case 'automated_reviewer': {
      const opts = options as UnifiedHovercardOptions<'automated_reviewer'>
      const reviewer = 'botSlug' in opts ? getAutomatedReviewer(opts.botSlug) : undefined
      return reviewer ? automatedReviewerHovercardPath({reviewer: reviewer.hovercard}) : null
    }
    case 'repository': {
      const opts = options as UnifiedHovercardOptions<'repository'>
      if ('owner' in opts && opts.owner && 'repo' in opts && opts.repo) {
        return repositoryPath({owner: opts.owner, repo: opts.repo, action: 'hovercard'})
      }
      return null
    }
    case 'discussion': {
      const opts = options as UnifiedHovercardOptions<'discussion'>
      if (
        'owner' in opts &&
        opts.owner &&
        'repo' in opts &&
        opts.repo &&
        'discussionNumber' in opts &&
        opts.discussionNumber != null
      ) {
        return discussionHovercardPath({owner: opts.owner, repo: opts.repo, discussionNumber: opts.discussionNumber})
      }
      return null
    }
    case 'advisory': {
      const opts = options as UnifiedHovercardOptions<'advisory'>
      if ('ghsaId' in opts && opts.ghsaId) return advisoryHovercardPath({ghsaId: opts.ghsaId})
      return null
    }
    case 'cwe': {
      const opts = options as UnifiedHovercardOptions<'cwe'>
      if ('id' in opts && opts.id) return cweHovercardPath({id: opts.id})
      return null
    }
    case 'sponsors_listing': {
      const opts = options as UnifiedHovercardOptions<'sponsors_listing'>
      if ('login' in opts && opts.login) return sponsorsListingHovercardPath({login: opts.login})
      return null
    }
    case 'achievement': {
      const opts = options as UnifiedHovercardOptions<'achievement'>
      if ('login' in opts && opts.login && 'slug' in opts && opts.slug) {
        return achievementHovercardPath({login: opts.login, slug: opts.slug})
      }
      return null
    }
    case 'tracked_in': {
      const opts = options as UnifiedHovercardOptions<'tracked_in'>
      if (
        'owner' in opts &&
        opts.owner &&
        'repo' in opts &&
        opts.repo &&
        'issueNumber' in opts &&
        opts.issueNumber != null
      ) {
        return trackedInHovercardPath({owner: opts.owner, repo: opts.repo, issueNumber: opts.issueNumber})
      }
      return null
    }
    case 'tracking': {
      const opts = options as UnifiedHovercardOptions<'tracking'>
      if (
        'owner' in opts &&
        opts.owner &&
        'repo' in opts &&
        opts.repo &&
        'issueNumber' in opts &&
        opts.issueNumber != null &&
        'id' in opts &&
        opts.id != null
      ) {
        return trackingHovercardPath({owner: opts.owner, repo: opts.repo, issueNumber: opts.issueNumber, id: opts.id})
      }
      return null
    }
    case 'security_alert': {
      const opts = options as UnifiedHovercardOptions<'security_alert'>
      if (
        'owner' in opts &&
        opts.owner &&
        'repo' in opts &&
        opts.repo &&
        'alertNumber' in opts &&
        opts.alertNumber != null
      ) {
        return securityAlertHovercardPath({owner: opts.owner, repo: opts.repo, alertNumber: opts.alertNumber})
      }
      return null
    }
    case 'dependabot_alert': {
      const opts = options as UnifiedHovercardOptions<'dependabot_alert'>
      if (
        'owner' in opts &&
        opts.owner &&
        'repo' in opts &&
        opts.repo &&
        'alertNumber' in opts &&
        opts.alertNumber != null
      ) {
        return dependabotAlertHovercardPath({owner: opts.owner, repo: opts.repo, alertNumber: opts.alertNumber})
      }
      return null
    }
    case 'dependency_graph_package': {
      const opts = options as UnifiedHovercardOptions<'dependency_graph_package'>
      if (
        'owner' in opts &&
        opts.owner &&
        'repo' in opts &&
        opts.repo &&
        'id' in opts &&
        opts.id &&
        'name' in opts &&
        opts.name
      ) {
        return dependencyGraphPackageHovercardPath({owner: opts.owner, repo: opts.repo, id: opts.id, name: opts.name})
      }
      return null
    }
    case 'project': {
      const opts = options as UnifiedHovercardOptions<'project'>
      if ('projectId' in opts && opts.projectId != null) return projectHovercardPath({projectId: opts.projectId})
      return null
    }
    case 'integration': {
      const opts = options as UnifiedHovercardOptions<'integration'>
      if ('id' in opts && opts.id != null) return integrationHovercardPath({id: opts.id})
      return null
    }
    case 'profile_highlight': {
      const opts = options as UnifiedHovercardOptions<'profile_highlight'>
      if ('login' in opts && opts.login && 'highlightType' in opts && opts.highlightType) {
        return profileHighlightHovercardPath({login: opts.login, highlightType: opts.highlightType})
      }
      return null
    }
    default: {
      return null
    }
  }
}

/**
 * React hook that returns a function for generating hovercard attributes.
 * Handles SSR and user preferences automatically.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const getHovercardAttributesForType = useGetHovercardAttributesForType()
 *
 *   return (
 *     <>
 *       <a {...getHovercardAttributesForType('user', {login: 'octocat'})} href="/octocat">
 *         @octocat
 *       </a>
 *       <a {...getHovercardAttributesForType('issue', {owner: 'github', repo: 'docs', issueNumber: 123})} href="/github/docs/issues/123">
 *         #123
 *       </a>
 *       <a {...getHovercardAttributesForType('repository', {owner: 'github', repo: 'docs', fixedPositioning: true})} href="/github/docs">
 *         github/docs
 *       </a>
 *     </>
 *   )
 * }
 * ```
 */
export function useGetHovercardAttributesForType(): GetHovercardAttributesForTypeFn {
  const {isServer} = useRuntimeEnvironment()
  const isDisabled = isServer || !isHovercardEnabledForUser()

  return useCallback(
    <T extends UnifiedHovercardType>(type: T, options: UnifiedHovercardOptions<T>): HovercardAttributesResult => {
      if (isDisabled) return EMPTY_RESULT

      const url = getHovercardUrlForType(type, options)
      if (!url) return EMPTY_RESULT

      const tracking = options.tracking ?? true
      const config: HovercardConfigOptions = {
        fixedPositioning: options.fixedPositioning,
        zIndexOverride: options.zIndexOverride,
        cache: options.cache,
      }

      return buildHovercardAttributesWithConfig(url, type, tracking, config)
    },
    [isDisabled],
  )
}
